/**
 * BattleshipGame — Main container component for the Battleship game
 *
 * Orchestrates game mode selection, engine initialization, placement phase,
 * attack phase, AI integration, and game completion. Wraps in ErrorBoundary.
 *
 * Requirements: 2.8, 6.1, 6.2, 9.3, 9.4, 9.5, 10.2, 14.6, 17.5, 17.6,
 *               19.3, 19.4, 19.7, 20.1, 20.4, 20.5, 20.6, 20.7, 20.8, 21.1
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { BattleshipEngine } from '../BattleshipEngine';
import { BattleshipAI } from '../BattleshipAI';
import { BattleshipBoard } from './BattleshipBoard';
import { ShipDock } from './ShipDock';
import { FleetStatusComponent as FleetStatus } from './FleetStatus';
import { AttackHistory } from './AttackHistory';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { GameCoachService, GameContext } from '../../services/GameCoachService';
import { battleshipKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { useAuthentication } from '../../hooks/useAuthentication';
import { GameSessionManager } from '../../services/GameSessionManager';
import UniversalGameLayout, { UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import {
  BattleshipGameProps,
  BattleshipGameSettings,
  BattleshipDifficulty,
  Coordinate,
  ShipPlacement,
  ShipDockItem,
  ShipType,
  Orientation,
  SHIP_SIZES,
  PlayerSide,
  AttackResult,
} from '../types';
import './BattleshipGame.css';

/** All five ship types in standard order */
const ALL_SHIP_TYPES: ShipType[] = [
  'carrier',
  'battleship',
  'cruiser',
  'submarine',
  'destroyer',
];

/** Display names for ship types */
const SHIP_DISPLAY_NAMES: Record<ShipType, string> = {
  carrier: 'Carrier',
  battleship: 'Battleship',
  cruiser: 'Cruiser',
  submarine: 'Submarine',
  destroyer: 'Destroyer',
};

// ============================================================================
// Inner component (unwrapped from ErrorBoundary)
// ============================================================================

const BattleshipGameInner: React.FC<BattleshipGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated } = useAuthentication();

  // Core game state
  const [engine] = useState<BattleshipEngine>(() => new BattleshipEngine());
  const [gameMode, setGameMode] = useState<'singleplayer' | 'multiplayer-realtime' | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<BattleshipDifficulty>('medium');
  const [ai] = useState(() => new BattleshipAI('medium'));
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [moveCount, setMoveCount] = useState(0);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [showResultOverlay, setShowResultOverlay] = useState(false);

  // Placement state
  const [shipOrientations, setShipOrientations] = useState<Record<ShipType, Orientation>>({
    carrier: 'horizontal',
    battleship: 'horizontal',
    cruiser: 'horizontal',
    submarine: 'horizontal',
    destroyer: 'horizontal',
  });
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // Mobile tab toggle for attack phase
  const [activeTab, setActiveTab] = useState<'ocean' | 'tracking'>('tracking');

  // Refs
  const aiMoveScheduled = useRef(false);
  const gameSessionId = useRef<string | null>(null);
  const coachRef = useRef(new GameCoachService('Battleship', battleshipKnowledge));

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const gamePhase = engine.getGamePhase();
  const currentTurn = engine.getCurrentTurn();
  const gameStatus = engine.getGameStatus();
  const attackHistory = engine.getAttackHistory();
  const isGameOver = engine.isGameOver();
  const winner = engine.getWinner();

  const playerFleetStatus = engine.getFleetStatus('player1');
  const opponentFleetStatus = engine.getFleetStatus('player2');

  const placedShips = engine.getPlacedShips('player1');
  const allShipsPlaced = engine.isFleetComplete('player1');

  // Build ship dock items
  const shipDockItems: ShipDockItem[] = ALL_SHIP_TYPES.map((type) => ({
    type,
    size: SHIP_SIZES[type],
    placed: placedShips.some((p) => p.shipType === type),
    orientation: shipOrientations[type],
  }));

  // -----------------------------------------------------------------------
  // Show result overlay when game ends
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (isGameOver && !showResultOverlay) {
      // Small delay for final animation
      const timer = setTimeout(() => setShowResultOverlay(true), 800);
      return () => clearTimeout(timer);
    }
  }, [isGameOver, showResultOverlay]);

  // -----------------------------------------------------------------------
  // Game mode handlers
  // -----------------------------------------------------------------------

  const handleStartGame = useCallback(
    (settings: BattleshipGameSettings) => {
      engine.initializeGame();
      setGameMode(settings.mode);
      setShowModeSelector(false);
      setMoveCount(0);
      setIsAIThinking(false);
      setShowResultOverlay(false);
      setIsPlayerReady(false);
      setActiveTab('tracking');
      aiMoveScheduled.current = false;

      if (settings.mode === 'singleplayer' && settings.aiDifficulty) {
        setAiDifficulty(settings.aiDifficulty);
        ai.setDifficulty(settings.aiDifficulty);
      }

      try {
        const sessionId = GameSessionManager.getInstance().startGameSession('battleship');
        gameSessionId.current = sessionId;
      } catch {
        // Non-critical
      }
    },
    [engine, ai]
  );

  const handleJoinPrivateGame = useCallback(async (roomCode: string) => {
    // Multiplayer join — placeholder for future WebSocket integration
    console.log('Joining room:', roomCode);
  }, []);

  // -----------------------------------------------------------------------
  // Placement handlers
  // -----------------------------------------------------------------------

  const handleShipDragStart = useCallback((_shipType: ShipType) => {
    // Visual feedback handled by CSS
  }, []);

  const handleRotate = useCallback(
    (shipType: ShipType) => {
      // If already placed, remove and re-place with new orientation
      const currentPlacement = placedShips.find((p) => p.shipType === shipType);
      if (currentPlacement) {
        engine.removeShip('player1', shipType);
        const newOrientation: Orientation =
          currentPlacement.orientation === 'horizontal' ? 'vertical' : 'horizontal';
        const newPlacement: ShipPlacement = {
          ...currentPlacement,
          orientation: newOrientation,
        };
        const validation = engine.isPlacementValid('player1', newPlacement);
        if (validation.valid) {
          engine.placeShip('player1', newPlacement);
        } else {
          // Re-place with original orientation if rotation invalid
          engine.placeShip('player1', currentPlacement);
        }
      }

      setShipOrientations((prev) => ({
        ...prev,
        [shipType]:
          prev[shipType] === 'horizontal' ? 'vertical' : 'horizontal',
      }));
      setMoveCount((c) => c + 1);
    },
    [engine, placedShips]
  );

  const handleAutoPlace = useCallback(() => {
    engine.autoPlaceShips('player1');
    setMoveCount((c) => c + 1);
  }, [engine]);

  const handleShipDrop = useCallback(
    (placement: ShipPlacement) => {
      const validation = engine.isPlacementValid('player1', placement);
      if (validation.valid) {
        engine.placeShip('player1', placement);
        setMoveCount((c) => c + 1);
      }
    },
    [engine]
  );

  const handleOceanCellClick = useCallback(
    (coordinate: Coordinate) => {
      if (gamePhase !== 'placement' || isPlayerReady) return;

      // Find the first unplaced ship and place it at this coordinate
      const unplacedShip = ALL_SHIP_TYPES.find(
        (type) => !placedShips.some((p) => p.shipType === type)
      );
      if (!unplacedShip) return;

      const placement: ShipPlacement = {
        shipType: unplacedShip,
        startCell: coordinate,
        orientation: shipOrientations[unplacedShip],
      };

      const validation = engine.isPlacementValid('player1', placement);
      if (validation.valid) {
        engine.placeShip('player1', placement);
        setMoveCount((c) => c + 1);
      }
    },
    [engine, gamePhase, isPlayerReady, placedShips, shipOrientations]
  );

  const handleReady = useCallback(() => {
    if (!allShipsPlaced) return;

    setIsPlayerReady(true);

    // Single-player: AI places ships and transition to attack phase
    if (gameMode === 'singleplayer') {
      ai.generatePlacement(engine);
      engine.transitionToAttackPhase();
      setMoveCount((c) => c + 1);
    }
  }, [allShipsPlaced, gameMode, engine, ai]);

  // -----------------------------------------------------------------------
  // Attack handlers
  // -----------------------------------------------------------------------

  const handleTrackingCellClick = useCallback(
    (coordinate: Coordinate) => {
      if (gamePhase !== 'attack') return;
      if (currentTurn !== 'player1') return;
      if (isAIThinking) return;
      if (isGameOver) return;

      const result: AttackResult = engine.processAttack('player1', coordinate);
      if (!result.success) return;

      setMoveCount((c) => c + 1);

      // If game is over after player's attack, stop
      if (result.gameOver) {
        if (gameSessionId.current) {
          try {
            GameSessionManager.getInstance().endGameSession(gameSessionId.current, true);
            gameSessionId.current = null;
          } catch {
            // Non-critical
          }
        }
        return;
      }

      // Single-player: trigger AI attack after a delay
      if (gameMode === 'singleplayer' && !aiMoveScheduled.current) {
        aiMoveScheduled.current = true;
        setIsAIThinking(true);

        const delay = aiDifficulty === 'easy' ? 500 : aiDifficulty === 'medium' ? 800 : 1200;

        setTimeout(() => {
          try {
            const trackingGrid = engine.getTrackingGrid('player2');
            const remainingShips = engine.getFleetStatus('player1').ships;
            const aiCoord = ai.selectAttackCoordinate(trackingGrid, remainingShips);
            const aiResult = engine.processAttack('player2', aiCoord);

            if (aiResult.gameOver && gameSessionId.current) {
              try {
                GameSessionManager.getInstance().endGameSession(gameSessionId.current, true);
                gameSessionId.current = null;
              } catch {
                // Non-critical
              }
            }
          } catch (error) {
            console.error('BattleshipGame: Error during AI attack:', error);
          } finally {
            setIsAIThinking(false);
            aiMoveScheduled.current = false;
            setMoveCount((c) => c + 1);
          }
        }, delay);
      }
    },
    [engine, gamePhase, currentTurn, isAIThinking, isGameOver, gameMode, ai, aiDifficulty]
  );

  // -----------------------------------------------------------------------
  // New game / exit
  // -----------------------------------------------------------------------

  const handleNewGame = useCallback(() => {
    engine.initializeGame();
    setGameMode(null);
    setShowModeSelector(true);
    setMoveCount(0);
    setIsAIThinking(false);
    setShowResultOverlay(false);
    setIsPlayerReady(false);
    setActiveTab('tracking');
    aiMoveScheduled.current = false;
    setShipOrientations({
      carrier: 'horizontal',
      battleship: 'horizontal',
      cruiser: 'horizontal',
      submarine: 'horizontal',
      destroyer: 'horizontal',
    });
  }, [engine]);

  // -----------------------------------------------------------------------
  // Display helpers
  // -----------------------------------------------------------------------

  const getTurnText = (): string => {
    if (isGameOver) {
      if (winner === 'player1') return '🎉 You Win!';
      return '😔 You Lost!';
    }

    if (gamePhase === 'placement') {
      if (isPlayerReady) return 'Waiting for opponent...';
      return 'Place your ships';
    }

    if (isAIThinking) return "AI is attacking...";
    if (currentTurn === 'player1') return 'Your Turn — Select a target';
    return "Opponent's Turn";
  };

  const getHeaderTitle = (): string => {
    if (gameMode === 'singleplayer') {
      const diffLabel = aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1);
      return `Battleship vs AI — ${diffLabel}`;
    }
    if (gameMode === 'multiplayer-realtime') return 'Battleship — Online';
    return 'Battleship';
  };

  const getGameStats = () => {
    const totalAttacks = attackHistory.length;
    const playerAttacks = attackHistory.filter((a) => a.attacker === 'player1');
    const playerHits = playerAttacks.filter(
      (a) => a.result === 'hit' || a.result === 'sunk'
    );
    const hitPercentage =
      playerAttacks.length > 0
        ? Math.round((playerHits.length / playerAttacks.length) * 100)
        : 0;
    const shipsSunk = opponentFleetStatus.shipsSunk;

    return { totalAttacks, hitPercentage, shipsSunk };
  };

  // -----------------------------------------------------------------------
  // Render: Mode selector
  // -----------------------------------------------------------------------

  if (showModeSelector) {
    const battleshipConfig: GameModeConfig = {
      gameName: 'Battleship',
      gameIcon: '🚢',
      availableModes: ['ai', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI attacks randomly' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'AI hunts after hits' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'AI uses advanced targeting' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as BattleshipDifficulty,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true });
      }
    };

    return (
      <div className="bs-game-container">
        <UniversalGameModeSelector
          config={battleshipConfig}
          onStart={handleModeSelection}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true })}
          onJoinRoom={handleJoinPrivateGame}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={
            isAuthenticated
              ? undefined
              : GameSessionManager.getInstance().getRemainingFreeGames()
          }
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Placement Phase
  // -----------------------------------------------------------------------

  if (gamePhase === 'placement') {
    const placementConfig: UniversalGameLayoutConfig = {
      gameName: 'Battleship',
      board: (
        <div className="bs-game-placement">
          <div className="bs-game-placement__dock">
            <ShipDock
              ships={shipDockItems}
              onShipDragStart={handleShipDragStart}
              onRotate={handleRotate}
              onAutoPlace={handleAutoPlace}
              onReady={handleReady}
              allShipsPlaced={allShipsPlaced}
              isReady={isPlayerReady}
            />
          </div>
          <div className="bs-game-placement__board">
            <BattleshipBoard
              engine={engine}
              gridType="ocean"
              playerSide="player1"
              interactive={!isPlayerReady}
              isPlayerTurn={true}
              gamePhase={gamePhase}
              onCellClick={handleOceanCellClick}
              onShipDrop={handleShipDrop}
            />
          </div>
        </div>
      ),
      statusBar: {
        turnIndicator: <span>{getTurnText()}</span>,
        gameStatus: isPlayerReady ? 'Waiting for opponent...' : 'Place your ships',
        gameStatusType: 'active',
      },
      actions: [
        { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
        ...(onExit ? [{ id: 'exit', label: 'Exit', icon: '🚪', onClick: onExit, variant: 'secondary' as const }] : []),
      ],
    };

    return (
      <div className="bs-game-container">
        <UniversalGameLayout config={placementConfig} />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Attack Phase / Completed
  // -----------------------------------------------------------------------

  const stats = getGameStats();

  const attackBoard = (
    <div className="bs-game-attack">
      {/* Mobile Tab Toggle */}
      <div className="bs-grid-tabs">
        <div className="bs-grid-tabs__container">
          <button
            className={`bs-grid-tab ${activeTab === 'ocean' ? 'bs-grid-tab--active' : ''}`}
            onClick={() => setActiveTab('ocean')}
          >
            Your Fleet
          </button>
          <button
            className={`bs-grid-tab ${activeTab === 'tracking' ? 'bs-grid-tab--active' : ''}`}
            onClick={() => setActiveTab('tracking')}
          >
            Enemy Waters
          </button>
        </div>
      </div>

      <div className="bs-game-attack__boards">
        {/* Ocean Grid (player's fleet) */}
        <div
          className={`bs-game-attack__board ${
            activeTab !== 'ocean' ? 'bs-game-attack__board--hidden' : ''
          }`}
        >
          <BattleshipBoard
            engine={engine}
            gridType="ocean"
            playerSide="player1"
            interactive={false}
            isPlayerTurn={false}
            gamePhase={gamePhase}
          />
        </div>

        {/* Tracking Grid (attacks on opponent) */}
        <div
          className={`bs-game-attack__board ${
            activeTab !== 'tracking' ? 'bs-game-attack__board--hidden' : ''
          }`}
        >
          <BattleshipBoard
            engine={engine}
            gridType="tracking"
            playerSide="player1"
            interactive={!isGameOver && !isAIThinking}
            isPlayerTurn={currentTurn === 'player1'}
            gamePhase={gamePhase}
            onCellClick={handleTrackingCellClick}
          />
        </div>
      </div>
    </div>
  );

  // Left panels: Fleet Status + Attack History
  const leftPanels = [
    {
      id: 'fleet-status',
      title: 'Fleet Status',
      icon: '🚢',
      content: (
        <FleetStatus
          playerFleet={playerFleetStatus}
          opponentFleet={opponentFleetStatus}
          showOpponentShipNames={isGameOver}
        />
      ),
    },
    {
      id: 'attack-history',
      title: 'Attack Log',
      icon: '📜',
      content: (
        <AttackHistory
          attacks={attackHistory}
          currentMoveIndex={attackHistory.length - 1}
        />
      ),
    },
  ];

  // Right panels: Game Coach (single-player only)
  const rightPanels = gameMode === 'singleplayer' ? [
    {
      id: 'coach',
      title: 'AI Coach',
      icon: '🤖',
      content: (
        <GameCoach
          coach={coachRef.current}
          gameContext={{
            gameName: 'Battleship',
            gameState: `Phase: ${gamePhase}. Attacks: ${attackHistory.length}. Hit accuracy: ${stats.hitPercentage}%. Ships sunk: ${stats.shipsSunk}/5.`,
            playerContext: `${currentTurn === 'player1' ? "It's your turn to attack." : "Waiting for AI attack."}`,
          }}
          isPlayerTurn={currentTurn === 'player1' && !isAIThinking}
          quickQuestions={['Where to shoot?', 'Search pattern?', 'Ship placement?', 'After a hit?']}
          accentColor="#1e40af"
        />
      ),
    },
  ] : [];

  // Game over config
  const gameOverConfig = showResultOverlay && isGameOver ? {
    show: true,
    result: (winner === 'player1' ? 'win' : 'loss') as 'win' | 'loss',
    title: winner === 'player1' ? 'Victory!' : 'Defeat',
    subtitle: `Attacks: ${stats.totalAttacks} | Accuracy: ${stats.hitPercentage}% | Sunk: ${stats.shipsSunk}/5`,
    icon: winner === 'player1' ? '🏆' : '💥',
    onPlayAgain: handleNewGame,
    onExit: onExit || handleNewGame,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Battleship',
    board: attackBoard,
    leftPanels,
    rightPanels,
    statusBar: {
      turnIndicator: <span>{getTurnText()}</span>,
      gameStatus: isGameOver ? (winner === 'player1' ? 'Victory!' : 'Defeat') : undefined,
      gameStatusType: isGameOver ? 'ended' : 'active',
      aiThinking: isAIThinking,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      ...(onExit ? [{ id: 'exit', label: 'Exit', icon: '🚪', onClick: onExit, variant: 'secondary' as const }] : []),
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="bs-game-container">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

// ============================================================================
// Exported component wrapped in ErrorBoundary
// ============================================================================

export const BattleshipGame: React.FC<BattleshipGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="Battleship">
      <BattleshipGameInner {...props} />
    </ErrorBoundary>
  );
};

export default BattleshipGame;
